// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { TABBAR_ITEM_TRANSITION } from '../../../../../constants/ui/tabbar';
import {
  BoomHalloweenIcon,
  BoomHalloweenIconActive,
  BoomPatrickIcon,
  BoomPatrickIconActive,
  BoomValentineIcon,
  BoomValentineIconActive,
} from '../../../assets/svg';
import { BoomSkinIds, type BoomSkinId } from '../../../types/global';

const bounceAnimation = keyframes`
  47% {
    transform: scale(1);
    filter: brightness(100%);
  }

  50% {
    transform: scale(1.2);
    filter: brightness(200%);
  }

  53% {
    transform: scale(1);
    filter: brightness(100%);
  }
`;

const Wrapper = styled.div`
  width: var(--BoomNavIcon-width, 1.8em);
  height: var(--BoomNavIcon-height, 1.8em);
  margin: 0;
  padding: 0;

  position: relative;
  overflow: hidden;

  transition: filter ${TABBAR_ITEM_TRANSITION}ms ease-in-out;

  ${({ $animate }) => css`
    ${$animate
      ? css`
          animation: ${bounceAnimation} 3000ms ease-in-out infinite;
        `
      : undefined}
  `}
`;

const StyledSvg = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  width: 100%;

  z-index: 1;
`;

const DefaultStyledSvg = css`
  ${StyledSvg}

  transition: color ${TABBAR_ITEM_TRANSITION}ms ease-in-out;
  ${({ $isShowing }) => css`
    color: ${$isShowing ? '#737373' : 'transparent'};
  `}
`;

const ActiveStyledSvg = css`
  ${StyledSvg}

  transition: opacity ${TABBAR_ITEM_TRANSITION}ms ease-in-out;
  ${({ $isShowing }) => css`
    opacity: ${$isShowing ? 1 : 0};
  `}
`;

const StyledBoomHalloweenIcon = styled(BoomHalloweenIcon)`
  ${DefaultStyledSvg}
`;

const StyledBoomHalloweenActiveIcon = styled(BoomHalloweenIconActive)`
  ${ActiveStyledSvg}
`;

const StyledBoomValentineIcon = styled(BoomValentineIcon)`
  ${DefaultStyledSvg}
`;

const StyledBoomValentineActiveIcon = styled(BoomValentineIconActive)`
  ${ActiveStyledSvg}
`;

const StyledBoomPatrickIcon = styled(BoomPatrickIcon)`
  ${DefaultStyledSvg};
`;

const StyledBoomPatrickActiveIcon = styled(BoomPatrickIconActive)`
  ${ActiveStyledSvg}
`;

type BoomNavIconProps = {
  className?: string;
  disableAnimation?: boolean;
  isActive?: boolean;
  skinId?: BoomSkinId;
  'data-testid'?: string;
};

const BoomNavIcon = ({
  className = '',
  disableAnimation = false,
  isActive = false,
  skinId = BoomSkinIds.HALLOWEEN,
  'data-testid': dataTestId,
}: BoomNavIconProps) => {
  const DefaultIcon = useMemo(() => {
    switch (skinId) {
      case BoomSkinIds.VALENTINE:
        return StyledBoomValentineIcon;
      case BoomSkinIds.PATRICK:
        return StyledBoomPatrickIcon;
      case BoomSkinIds.HALLOWEEN:
      default:
        return StyledBoomHalloweenIcon;
    }
  }, [skinId]);

  const ActiveIcon = useMemo(() => {
    switch (skinId) {
      case BoomSkinIds.VALENTINE:
        return StyledBoomValentineActiveIcon;
      case BoomSkinIds.PATRICK:
        return StyledBoomPatrickActiveIcon;
      case BoomSkinIds.HALLOWEEN:
      default:
        return StyledBoomHalloweenActiveIcon;
    }
  }, [skinId]);

  return (
    <Wrapper data-testid={dataTestId} className={className} $animate={!disableAnimation}>
      <ActiveIcon $isShowing={isActive} />
      <DefaultIcon $isShowing={!isActive} />
    </Wrapper>
  );
};

export default BoomNavIcon;
